import { queryOptions, QueryClient } from "@tanstack/react-query"

import { safeQueryOptions } from "@src/utils/query"
import { UnpackPromise, UnpackArray } from "@src/utils/types"
import { client } from "./client"

import { components } from "./schema"
import { KpiDefinition } from "./kpiDefinition"
import { userKeys } from "./user"

export const projectKeys = {
  all: ["projects"],
  list: () => [...projectKeys.all, "list"],
  byMethodology: (methodologyId?: number, communityId?: number) => [
    ...projectKeys.all,
    methodologyId,
    communityId,
    "byMethodology",
  ],
  // communityId is appended ONLY when defined, so that calling the factory
  // without it (e.g. in cross-community invalidations) yields a true prefix
  // that partial-matches every community-scoped variant. Appending a trailing
  // `undefined` instead would break TanStack Query's partialMatchKey.
  singleOverview: (id?: number | null, communityId?: number) =>
    communityId === undefined
      ? [...projectKeys.all, id, "overview"]
      : [...projectKeys.all, id, "overview", communityId],
  singleOverviewSummary: (id?: number, communityId?: number) =>
    communityId === undefined
      ? [...projectKeys.all, id, "overviewSummary"]
      : [...projectKeys.all, id, "overviewSummary", communityId],
  listOverview: (filters: CommunityProjectsFilters) => [
    ...projectKeys.all,
    "overview",
    filters,
  ],
  mapPinsByCommunity: (filters: CommunityProjectMapPinsFilter) => [
    ...projectKeys.all,
    "mapPinsByCommunity",
    filters,
  ],
  trackingSearchOverview: (query: ProjectTrackingSearchOverviewQuery) => [
    ...projectKeys.all,
    "trackingSearchOverview",
    query,
  ],
  // communityId trailing + appended only when defined (see singleOverview note)
  // so by-project invalidation [...all, projectId, "circles"] matches all variants.
  projectCircles: (projectId?: number, communityId?: number) =>
    communityId === undefined
      ? [...projectKeys.all, projectId, "circles"]
      : [...projectKeys.all, projectId, "circles", communityId],
  projectUsers: (filters: ProjectUserFilter) => [
    ...projectKeys.all,
    filters.projectId,
    "users",
    filters,
  ],
  projectUsersAll: (projectId?: number) => [
    ...projectKeys.all,
    projectId,
    "users",
  ],
  // communityId trailing + appended only when defined (see singleOverview note)
  // so a no-community invalidation [...all, projectId, "kpiDefinition"] stays a
  // valid prefix that partial-matches every community-scoped variant.
  kpiDefinition: (projectId?: number, communityId?: number) =>
    communityId === undefined
      ? [...projectKeys.all, projectId, "kpiDefinition"]
      : [...projectKeys.all, projectId, "kpiDefinition", communityId],
  methodologyList: (projectId?: number, communityId?: number) =>
    communityId === undefined
      ? [...projectKeys.all, projectId, "methodologyList"]
      : [...projectKeys.all, projectId, "methodologyList", communityId],
  requestFeedbackForProject: (projectId?: number) => [
    ...projectKeys.all,
    projectId,
    "requestFeedback",
  ],
  trackingDetails: (
    projectId: number,
    methodologyId: number,
    communityId?: number,
  ) => [
    ...projectKeys.all,
    projectId,
    "trackingDetails",
    methodologyId,
    communityId,
  ],
  assignUserToCircle: (params?: GetProjectListToAssignUserToCircleParams) => [
    ...projectKeys.all,
    "assignUserToCircle",
    params?.communityId,
    params?.userId,
    params?.projectName,
    params?.take,
    params?.skip,
  ],
}

export type ProjectOverview = UnpackPromise<
  ReturnType<typeof getProjectOverview>
>

export type ProjectDto = components["schemas"]["Project"]
export type ProjectOverviewDto = components["schemas"]["ProjectOverviewDto"]

export const getProjectOverview = async (
  projectId: number,
  communityId?: number,
) => {
  const data = await client.GET("/api/Project/GetOverview", {
    params: {
      query: { id: projectId, communityId },
    },
  })

  return data
}

export const getProjectOverviewQuery = (
  projectId?: number,
  communityId?: number,
) =>
  safeQueryOptions({
    queryFn: (id: number) => getProjectOverview(id, communityId),
    queryKeys: projectKeys.singleOverview(projectId, communityId),
    unsafeQueryFnArgs: [projectId],
  })

export const getProjectsByMethodology = async (
  methodologyId: number,
  communityId: number,
) => {
  const data = await client.GET("/api/Project/GetByMethodology", {
    params: {
      query: { methodologyId, communityId },
    },
  })

  return (data || []) as ProjectDto[]
}

export const getProjectsByMethodologyQuery = (
  methodologyId?: number,
  communityId?: number,
) =>
  safeQueryOptions({
    queryFn: getProjectsByMethodology,
    queryKeys: projectKeys.byMethodology(methodologyId, communityId),
    unsafeQueryFnArgs: [methodologyId, communityId],
  })

export type CommunityProjectsFilters = Omit<
  NonNullable<components["schemas"]["CommunityProjectDynamicFilter"]>,
  "approvedProjectsIdsInFollowedCommunities" | "followedProjectIds"
>

export const getProjectsOverview = async (
  filters: CommunityProjectsFilters,
) => {
  const data = await client.POST("/api/Project/GetByCommunity", {
    body: filters,
  })

  return data
}

export type CommunityProjectsListOverview = UnpackPromise<
  ReturnType<typeof getProjectsOverview>
>

export type CommunityProjectOverview = NonNullable<
  UnpackArray<NonNullable<CommunityProjectsListOverview["projects"]>["list"]>
>

export const getProjectsOverviewQuery = (filters: CommunityProjectsFilters) =>
  queryOptions({
    queryFn: () => getProjectsOverview(filters),
    queryKey: projectKeys.listOverview(filters),
    // 7 days cache time. This can be a ridiculously slow endpoint, so we're giving it a very high GC time
    // the idea is that we want to keep the data around for a long time, but we want to refresh it in the background
    // and to a "stealth" refresh without showing a loading indicator
    gcTime: 1000 * 60 * 60 * 24 * 7,
  })

export type ProjectMapPinDto = components["schemas"]["ProjectMapPinDto"]
export type CommunityProjectMapPinsFilter = Omit<
  CommunityProjectsFilters,
  "skip" | "take"
>

export const getProjectMapPinsByCommunity = async (
  filter: CommunityProjectMapPinsFilter,
) => {
  const data = await client.POST("/api/Project/GetMapPinsByCommunity", {
    body: filter,
  })

  return (data || []) as ProjectMapPinDto[]
}

export const getProjectMapPinsByCommunityQuery = (
  filter: CommunityProjectMapPinsFilter,
) =>
  queryOptions({
    queryFn: () => getProjectMapPinsByCommunity(filter),
    queryKey: projectKeys.mapPinsByCommunity(filter),
  })

export type ProjectTrackingSearchOverviewQuery = {
  CommunityId?: number
  MethodologyIds?: number[]
  Concept?: string
  CountryId?: number
  CategoryId?: number
  Tags?: string[]
  SustainableDevelopmentGoalsIds?: number[]
  Take?: number
  Skip?: number
}

export type ProjectUser = components["schemas"]["ProjectUserForListDto"]
export type ProjectCircle = components["schemas"]["ProjectCircle"]

export type ProjectTrackingOverview = {
  projectId: number
  projectName: string
  logoImage: string
  progress: number
  tags: string[]
  projectCircles: ProjectCircle[]
  projectUsers: ProjectUser[]
  lastUpdate: string | null
  stage: string
  coachingSessions: number
  kpi: number
}

export const getProjectTrackingSearchOverview = async (
  query: ProjectTrackingSearchOverviewQuery,
) => {
  const data = await client.GET("/api/ProjectTracking/SearchOverview", {
    params: {
      query,
    },
  })

  // Another failure of the schema, the response is not typed correctly :(
  return data as unknown as ProjectTrackingOverview[]
}

export const getProjectTrackingSearchOverviewQuery = (
  query: ProjectTrackingSearchOverviewQuery,
) =>
  queryOptions({
    queryFn: () => getProjectTrackingSearchOverview(query),
    queryKey: projectKeys.trackingSearchOverview(query),
    // 7 days cache time. This can be a ridiculously slow endpoint, so we're giving it a very high GC time
    // the idea is that we want to keep the data around for a long time, but we want to refresh it in the background
    // and to a "stealth" refresh without showing a loading indicator
    gcTime: 1000 * 60 * 60 * 24 * 7,
  })

export type ProjectCircleDto = components["schemas"]["ProjectCircle"]

export const getProjectCircles = async (
  projectId: number,
  communityId?: number,
) => {
  const data = await client.GET("/api/ProjectCircle/GetByProject", {
    params: {
      query: { projectId, ...(communityId !== undefined && { communityId }) },
    },
  })

  // Another failure of the schema, the response is not typed correctly :(
  return data as unknown as ProjectCircleDto[]
}

export const getProjectCirclesQuery = (
  projectId?: number,
  communityId?: number,
) =>
  safeQueryOptions({
    queryFn: (id: number) => getProjectCircles(id, communityId),
    queryKeys: projectKeys.projectCircles(projectId, communityId),
    unsafeQueryFnArgs: [projectId],
  })

export type ProjectUserFilter = components["schemas"]["ProjectUserFilter"]
export type ProjectUserListDto = components["schemas"]["ProjectUserListDto"]
export type ProjectUserForListDto =
  components["schemas"]["ProjectUserForListDto"]

export const getProjectUserList = async (filters: ProjectUserFilter) => {
  const data = await client.POST("/api/Project/GetUserList", {
    body: filters,
  })

  // Another failure of the schema, the response is not typed correctly :(
  return data as unknown as components["schemas"]["ProjectUserListDto"]
}

export const getProjectUserListQuery = (filters: ProjectUserFilter) =>
  queryOptions({
    queryFn: () => getProjectUserList(filters),
    queryKey: projectKeys.projectUsers(filters),
  })

export const applyToTeam = async (projectId: number) => {
  await client.GET("/api/Project/ApplyToTeam", {
    params: {
      query: { id: projectId },
    },
  })
}

export const approveApplyToTeamRequest = async (
  projectId: number,
  userId: number,
  communityId?: number,
) => {
  await client.GET("/api/Project/ApproveApplyToTeamRequest", {
    params: {
      query: {
        projectId,
        userId,
        ...(communityId !== undefined && { communityId }),
      },
    },
  })
}

export const denyApplyToTeamRequest = async (
  projectId: number,
  userId: number,
  communityId?: number,
) => {
  await client.GET("/api/Project/DenyApplyToTeamRequest", {
    params: {
      query: {
        projectId,
        userId,
        ...(communityId !== undefined && { communityId }),
      },
    },
  })
}

export const getKpiDefinitionForProject = async (
  projectId: number,
  communityId?: number,
) => {
  const data = await client.GET("/api/KpiDefinition/GetByProject", {
    params: {
      query: {
        projectId,
        communityId,
      },
    },
  })

  return data as unknown as KpiDefinition[]
}

export type ProjectTrackingDetailDto = ProjectTrackingOverviewDto & {
  sections: ProjectTrackingDetailSectionDto[]
}

export type ProjectTrackingOverviewDto = {
  projectId: number
  projectName: string
  projectLogo: string
  progress: number
}

export type ProjectTrackingDetailSectionDto = {
  sectionId: number
  sectionName: string
  sectionIcon: string
  sectionIsStagegateLocked: boolean
  paragraphs: ProjectTrackingDetailParagraphDto[]
}

export type ProjectTrackingDetailParagraphDto = {
  paragraphId: number
  paragraphName: string
  isPublished: boolean
  isApproved: boolean
  taskDateStart?: string | null
  taskDateEnd?: string | null
  approvedTime?: string | null
  submitedTime?: string | null
  involvedUsers: ProjectTrackingDetailParagraphUserDto[]
}

export type ProjectTrackingDetailParagraphUserDto = {
  userId: number
  userPhoto: string
  userFirstName: string
  userLastName: string
}

export const getKpiDefinitionForProjectQuery = (
  projectId?: number,
  communityId?: number,
) =>
  safeQueryOptions({
    queryFn: (id: number) => getKpiDefinitionForProject(id, communityId),
    queryKeys: projectKeys.kpiDefinition(projectId, communityId),
    unsafeQueryFnArgs: [projectId],
  })

export const getProjectTrackingDetails = async (
  projectId: number,
  methodologyId: number,
  communityId?: number,
) => {
  const data = await client.GET("/api/ProjectTracking/Details", {
    params: {
      query: { projectId, methodologyId, communityId },
    },
  })

  return data as unknown as ProjectTrackingDetailDto
}

export const getProjectTrackingDetailsQuery = (
  projectId: number,
  methodologyId: number,
  communityId?: number,
) =>
  queryOptions({
    queryFn: () =>
      getProjectTrackingDetails(projectId, methodologyId, communityId),
    queryKey: projectKeys.trackingDetails(
      projectId,
      methodologyId,
      communityId,
    ),
  })

export const getProjectOverviewSummary = async (
  projectId: number,
  communityId?: number,
) => {
  const data = await client.GET("/api/Project/GetOverviewSummary", {
    params: {
      query: { id: projectId, communityId },
    },
  })

  return data
}

export const getProjectOverviewSummaryQuery = (
  projectId?: number,
  communityId?: number,
) =>
  safeQueryOptions({
    queryFn: (id: number) => getProjectOverviewSummary(id, communityId),
    queryKeys: projectKeys.singleOverviewSummary(projectId, communityId),
    unsafeQueryFnArgs: [projectId],
  })

export type ProjectMethodologyDto =
  components["schemas"]["ProjectMethodologyDto"]

export const getMethodologyListByProject = async (
  projectId: number,
  communityId: number,
) => {
  const data = await client.GET(
    "/api/ProjectMethodology/ListMethodologiesByProject/{projectId}",
    {
      params: {
        path: { projectId },
        query: { communityId },
      },
    },
  )

  return data
}

export const getMethodologyListByProjectQuery = (
  projectId?: number,
  communityId?: number,
) =>
  safeQueryOptions({
    queryFn: getMethodologyListByProject,
    queryKeys: projectKeys.methodologyList(projectId, communityId),
    unsafeQueryFnArgs: [projectId, communityId],
  })

export const requestFeedbackForProject = async (
  communityId: number,
  projectId: number,
) => {
  const data = await client.GET(
    "/api/Request/{communityId}/feedback/{projectId}",
    {
      params: {
        path: { communityId, projectId },
      },
    },
  )
  return data
}

export const requestFeedbackForProjectQuery = (
  communityId: number,
  projectId?: number,
) =>
  safeQueryOptions({
    queryFn: requestFeedbackForProject,
    queryKeys: projectKeys.requestFeedbackForProject(projectId),
    unsafeQueryFnArgs: [communityId, projectId],
  })

export type EditProjectTagsDto = components["schemas"]["EditProjectTagsDto"]

export const saveProjectTags = async (request: EditProjectTagsDto) => {
  await client.POST("/api/Project/SaveTags", {
    body: request,
  })
}

export const addMethodologyToProject = async (
  projectId: number,
  methodologyId: number,
  communityId: number,
) => {
  await client.POST("/api/ProjectMethodology/Create", {
    body: { projectId, methodologyId, communityId },
  })
}

export const removeMethodologyFromProject = async (
  projectId: number,
  methodologyId: number,
  communityId: number,
) => {
  await client.DELETE("/api/ProjectMethodology/{projectId}/{methodologyId}", {
    params: {
      path: { projectId, methodologyId },
      query: { communityId },
    },
  })
}

export type SaveProjectCircleRequest = components["schemas"]["ProjectCircle"]

/**
 * Upsert: `id: 0` creates, a real `id` renames. On a rename the server takes the circle's
 * scope from the persisted row and ignores `projectId` / `communityProjectId` in the body,
 * so a rename can't move a circle between a project and a community.
 */
export const saveProjectCircle = async (request: SaveProjectCircleRequest) => {
  await client.POST("/api/ProjectCircle/Save", {
    body: request,
  })
}

export const removeProjectCircle = async (circleId: number) => {
  await client.GET("/api/ProjectCircle/Remove", {
    params: {
      query: { id: circleId },
    },
  })
}

export const acceptProjectInvitation = async (
  projectId: number,
  userId: number,
  communityId?: number,
) => {
  await client.GET("/api/Project/AcceptInvitation", {
    params: {
      query: {
        projectId,
        userId,
        ...(communityId !== undefined && { communityId }),
      },
    },
  })
}

export const declineProjectInvitation = async (
  projectId: number,
  userId: number,
  communityId?: number,
) => {
  await client.GET("/api/Project/DeclineInvitation", {
    params: {
      query: {
        projectId,
        userId,
        ...(communityId !== undefined && { communityId }),
      },
    },
  })
}

export const removeProjectInvitation = async (
  projectId: number,
  userId: number,
  email?: string,
  communityId?: number,
) => {
  await client.GET("/api/Project/RemoveInvitation", {
    params: {
      query: {
        projectId,
        userId,
        email,
        ...(communityId !== undefined && { communityId }),
      },
    },
  })
}

export type ProjectCommunityCircleDto =
  components["schemas"]["ProjectCommunityCircleDto"]

export const updateProjectCommunityCirclesPrivacy = async (
  request: ProjectCommunityCircleDto,
) => {
  await client.POST("/api/Project/UpdateProjectCommunityCircle", {
    body: request,
  })
}

export const followProject = async (projectId: number, communityId: number) => {
  await client.GET("/api/Project/Follow", {
    params: {
      query: { id: projectId, communityId },
    },
  })
}

export const unfollowProject = async (
  projectId: number,
  communityId: number,
) => {
  await client.GET("/api/Project/Unfollow", {
    params: {
      query: { id: projectId, communityId },
    },
  })
}

export type ProjectEditInfoDto = components["schemas"]["ProjectEditInfoDto"]
export type ProjectCreationInfoDto =
  components["schemas"]["ProjectCreationInfoDto"]

export const saveProjectEditInfoOverview = async (data: ProjectEditInfoDto) => {
  const response = await client.POST("/api/Project/SaveEditInfoOverview", {
    body: {
      ...data,
      websiteUrl: data.websiteUrl ?? "",
      twitterUrlSuffix: data.twitterUrlSuffix ?? "",
      facebookUrlSuffix: data.facebookUrlSuffix ?? "",
      googleUrlSuffix: data.googleUrlSuffix ?? "",
      linkedInUrlSuffix: data.linkedInUrlSuffix ?? "",
      flickrUrlSuffix: data.flickrUrlSuffix ?? "",
    },
  })
  return response
}

export const createProject = async (data: ProjectCreationInfoDto) => {
  const response = await client.POST("/api/Project/Create", {
    body: data,
  })
  return response
}

export type ProjectSimpleDto = components["schemas"]["ProjectSimpleDto"]

export type GetProjectListToAssignUserToCircleParams = {
  communityId: number
  userId: number
  projectName?: string
  take?: number
  skip?: number
}

export const getProjectListToAssignUserToCircle = async (
  params: GetProjectListToAssignUserToCircleParams,
) => {
  const data = await client.GET(
    "/api/Project/GetProjectListToAssignUserToCircle",
    {
      params: {
        query: {
          communityId: params.communityId,
          userId: params.userId,
          projectName: params.projectName,
          take: params.take,
          skip: params.skip,
        },
      },
    },
  )

  return (data || []) as ProjectSimpleDto[]
}

export const getProjectListToAssignUserToCircleQuery = (
  params: GetProjectListToAssignUserToCircleParams,
) =>
  queryOptions({
    queryFn: () => getProjectListToAssignUserToCircle(params),
    queryKey: projectKeys.assignUserToCircle(params),
  })

// Admin functions
export type ProjectEditInfoAdminAreaDto =
  components["schemas"]["ProjectEditInfoAdminAreaDto"]

export const getAllProjects = async () => {
  const data = await client.GET("/api/Project/GetEnabled")
  return data
}

export const getAllProjectsQuery = () =>
  queryOptions({
    queryKey: projectKeys.list(),
    queryFn: getAllProjects,
  })

export const saveProjectAdminArea = async (
  project: ProjectEditInfoAdminAreaDto,
) => {
  await client.POST("/api/Project/SaveEditInfoAdminArea", {
    body: project,
  })
}

export const disableProject = async (id: number) => {
  await client.GET("/api/Project/Disable", {
    params: {
      query: {
        id,
      },
    },
  })
}

export const deleteProject = async (id: number) => {
  await client.DELETE("/api/Project/Delete", {
    params: {
      query: {
        id,
      },
    },
  })
}

// `MoveIntoCommunity` (the old 1:1 endpoint) was replaced by the N:M
// `AddToCommunity` route — a project can now belong to multiple communities,
// so "moving" a project is equivalent to adding it to the target community.
export const moveProjectToCommunity = async (
  projectId: number,
  toCommunity: number,
) => {
  await addProjectToCommunity({ projectId, targetCommunityId: toCommunity })
}

export type UserCircleInvitationDto =
  components["schemas"]["UserCircleInvitationDto"]

export const inviteUserToProject = async (
  projectId: number,
  invitation: UserCircleInvitationDto,
) => {
  await client.POST("/api/Project/Invite/{id}", {
    params: {
      path: {
        id: projectId,
      },
    },
    body: invitation,
  })
}

export const moveUserToProject = async (
  projectId: number,
  invitation: UserCircleInvitationDto,
) => {
  await client.POST("/api/Project/MoveUserToProject/{id}", {
    params: {
      path: {
        id: projectId,
      },
    },
    body: invitation,
  })
}

export const addProjectToCommunity = async ({
  projectId,
  targetCommunityId,
  inviteMembersToCircleId,
}: {
  projectId: number
  targetCommunityId: number
  /** When set, the project's team members are also invited to the target community into this circle. */
  inviteMembersToCircleId?: number
}) => {
  await client.POST("/api/Project/AddToCommunity", {
    params: { query: { projectId, targetCommunityId, inviteMembersToCircleId } },
  })
}

export const removeProjectFromCommunity = async ({
  projectId,
  communityId,
}: {
  projectId: number
  communityId: number
}) => {
  await client.POST("/api/Project/RemoveFromCommunity", {
    params: { query: { projectId, communityId } },
  })
}

/**
 * Centralized invalidation for project↔community membership mutations.
 * Covers: the project's overview/summary/circles, the user's MyStuff
 * (Projects[].communityIds shifts), and every variant of getProjectsOverviewQuery
 * (admin overview lists in both source and target communities).
 */
export const invalidateMembershipQueries = (
  queryClient: QueryClient,
  projectId: number,
) => {
  queryClient.invalidateQueries({
    queryKey: projectKeys.singleOverview(projectId),
  })
  queryClient.invalidateQueries({
    queryKey: projectKeys.singleOverviewSummary(projectId),
  })
  queryClient.invalidateQueries({
    queryKey: projectKeys.projectCircles(projectId),
  })
  queryClient.invalidateQueries({
    queryKey: [...projectKeys.all, "overview"],
  })
  queryClient.invalidateQueries({ queryKey: userKeys.currentStuff() })
}
